import { useEffect, useRef, useState } from 'react'
import { COLUMN_PRIORITY } from '../../theme/tokens'

/** `NARROW_BELOW` — the container width below which `isNarrow` is `true`.
 *
 *  🔴 The concept names NO threshold for `isNarrow` (§12 BW4:823 gives the return
 *  shape only). Taken is `COLUMN_PRIORITY.lg` (1024): the container width from
 *  which the first optional column step appears (§3.2.2:445-448). Derived from the
 *  concept's own step table, not invented — a second threshold of its own would be
 *  a second source for the same boundary.
 *
 *  🔴 **Corrected 2026-10-14 (Grill W2).** This line used to add *„Below it a table
 *  carries its `kern` columns only"* (§3.2.2:457-458). That is **no longer true:**
 *  since the G1b-Nachbesserung 1 the column choice is a per-column **FIT** (Konzept
 *  §3.2.2, Orchestrator-Entscheid 2026-10-14), and a table below 1024 carries `lg`
 *  columns whenever the fit admits them — 1280 → five columns, two of them `lg`.
 *  What `NARROW_BELOW` still names is the **`lg` THRESHOLD**: the width from which
 *  the first optional step would appear under the old threshold reading.
 *  `isNarrow` is the boolean form of „the `lg` threshold is not reached" — **NOT**
 *  of „only `kern` is visible".
 *  ⚠️ *Abgeleitet, nicht Owner-Wortlaut.* */
export const NARROW_BELOW = COLUMN_PRIORITY.lg

/** Haengt den Beobachter ab und raeumt ein offenes Bild weg.
 *
 *  Nimmt die vier Refs als Argumente, damit der Umhaenge-Effekt und der
 *  Abbau-Effekt DIESELBE Zeile benutzen: eine Funktion aus dem Komponentenkoerper
 *  waere bei `[]`-Abhaengigkeiten eine fehlende Abhaengigkeit (`exhaustive-deps`),
 *  Refs sind stabil. */
function abhaengen(beobachter, rahmen, anstehend, angehaengt) {
  beobachter.current?.disconnect()
  beobachter.current = null
  if (rahmen.current != null) cancelAnimationFrame(rahmen.current)
  rahmen.current = null
  anstehend.current = null
  angehaengt.current = null
}

/**
 * useContainerWidth — the INNER width of one container, watched (BW4, UI2 Teil 10, G0b.3).
 *
 * @description
 *   Reports how wide a container really is, so that a decision depending on the
 *   available room reads the CONTAINER and never the viewport (Konzept §3.1,
 *   NW3.1:548): the sidebar's customize mode moves the container by ±40 px while
 *   the viewport stays put — the two are not the same signal.
 *
 *   The measurement is throttled through `requestAnimationFrame` and carries a
 *   loop guard: a callback reporting the SAME width starts no further run
 *   (NW4.6:652). Without both, the browser reports "ResizeObserver loop completed
 *   with undelivered notifications" — a state update that changes the layout feeds
 *   the next observer callback, which changes the layout again.
 *
 *   `width` is `null` until the first measurement. That is "not yet measured", NOT
 *   "zero" — the same line `KpiRow.jsx:279-284` draws for the same reason. A
 *   container that reports 0 is not laid out (`display: none`: a closed tab, a
 *   collapsed section); the entry is discarded and the last known width stands
 *   instead of a zero (Hausform `KpiRow.jsx:300-307`).
 *
 *   🔴 **The stepwise takeover is NOT built here** (R16-W4, Nachbesserung 1). NW4.6:652
 *   asks for three things: the `requestAnimationFrame` throttle, the loop guard — both
 *   here — and *„der Kopf übernimmt eine gemessene Breite in Schritten von höchstens
 *   einer Stufe je Frame"* (§4.3:617-619, checked by PW3b:1005). That third one belongs
 *   to a CONSUMER, and §6.3:912 / NW6.5:930 give this hook exactly the three properties
 *   above. The owner is `TableViewport` (G0b.4), which takes the measured width and
 *   moves its own cap one step per frame; the plan records the assignment at G0b.4.
 *   This hook hands over the RAW width and jumps to it in ONE frame — deliberately.
 *
 *   🔴 **The threshold behind `isNarrow` is NOT named in the concept.** Taken is
 *   `COLUMN_PRIORITY.lg` (1024): the container width from which the first optional
 *   column step appears (§3.2.2:445-448). The number is therefore derived from the
 *   concept's own step table, not invented; a second threshold of its own would be
 *   a second source for the same boundary.
 *   ⚠️ **Corrected 2026-10-14 (Grill W2):** this paragraph used to add *„Below it a
 *   table carries its `kern` columns only"* (§3.2.2:457-458). Since the
 *   G1b-Nachbesserung 1 the column choice is a per-column FIT (Konzept §3.2.2,
 *   Orchestrator-Entscheid 2026-10-14), so a table below 1024 carries `lg` columns
 *   whenever the fit admits them. `isNarrow` marks the `lg` THRESHOLD, not „kern
 *   only" — see `NARROW_BELOW` above.
 *   ⚠️ *Abgeleitet, nicht Owner-Wortlaut.*
 *
 *   🔴 **`isNarrow` is the BOOLEAN form of „`lg` is not visible yet" — and nothing
 *   else** (R16-W3, Nachbesserung 1). It is NOT a device class: the house knows
 *   `isMobile` = `theme.breakpoints.down('sm')` (600 px) and `down('md')` (900 px),
 *   both at the VIEWPORT; `NARROW_BELOW` is a CONTAINER width and corresponds to
 *   ≈ 1400 px viewport (§3.2.2:445). A surface can see `isMobile === false` and
 *   `isNarrow === true` at the same time, and that is correct — §3.1 and NW3.6:553
 *   separate the device question from the room question on purpose. Nor is it the
 *   N9.6 trigger: the edge hint hangs on the OVERFLOW case
 *   `scrollWidth > clientWidth` (§3.4:513-514 names the condition as
 *   *„Container < Summe(`kern`)"* — a table size from `Σ COLUMN_MAX[kind].min`,
 *   not this constant). Both directions of that confusion fail: a table with wide
 *   `kern` columns overflows at 1200 while `isNarrow === false`, and a narrow table
 *   fits at 700 while `isNarrow === true`.
 *
 *   `isNarrow` is a plain boolean and is `false` while nothing is measured: the
 *   first paint is the caller's own default (Konzept §4.3 — the pattern width, not
 *   the narrowest), and a claim about a layout must not be made before the layout is
 *   known. Who needs the honest "unknown" reads `width === null`.
 *
 *   🔴 **The caller carries the pattern width until the first measurement** (R16-B1,
 *   Nachbesserung 1). §4.3:623-624 settles it for every surface: *„eine Fläche, die
 *   ihre Breite nicht kennt, benutzt die Vorgabe ihres Musters — nie 0, nie `100 %`"*.
 *   This hook delivers the honest `null` (it cannot know the pattern — that is
 *   `PageWidthContext`, G0b.2); the caller that feeds a width into a layout passes
 *   `width ?? CONTENT_WIDTH[muster] ?? CONTENT_WIDTH.reading` (R16-W-N2, Nachbesserung
 *   2). 🔴 The second `??` is NOT redundant: `CONTENT_WIDTH.full` is `null` (no fixed
 *   cap, §1.1:156), so the middle term falls through for a `full` page — and
 *   §4.3:614-616 names `CONTENT_WIDTH.reading` as the value for exactly that case.
 *   `useColumnPriority` (BW5) does the same on its own side with `CONTENT_WIDTH.reading`
 *   as the fallback, so a caller handing over the raw `null` still gets a real layout
 *   and not the narrowest one.
 *
 *   🔴 What this hook canNOT do under Jest: jsdom has no layout, so no test may
 *   assert a MEASURED width. What is tested is the observer's build/teardown, the
 *   throttle and the loop guard (§6.3:902-912, NW6.5:930). The threshold is pinned
 *   by setting `width` from the observer callback — a value the test supplies, not
 *   one the environment produced.
 *
 *   🔴 **The observer follows the element, not the ref object** (R15-Warnung 3,
 *   Nachbesserung 1). An effect bound to `[ref]` reads `ref.current` exactly ONCE:
 *   a container that is rendered conditionally (`{open && <div ref={ref} />}` — the
 *   normal case inside a card or a tab) is mounted AFTER that one run, and the
 *   observer would never be attached. The effect below therefore runs after EVERY
 *   render and re-attaches as soon as `ref.current` carries a different element.
 *   The signature stays `ref` (BW4:823); no callback ref, no second source.
 *
 *   🔴 **On a container CHANGE the last known width STANDS until the new observer
 *   reports** (R16-W-N3, Nachbesserung 2). Re-attaching resets the loop guard
 *   (`zuletztGemessen`), **not** the STATE `width`: while the new observer's first
 *   delivery is pending (one to two frames), the hook keeps reporting the OLD
 *   container's width. That is deliberate — resetting `width` to `null` on the swap
 *   would flip `isNarrow` to `false` for a frame, exactly the first-paint jump R16-B1
 *   removes. The new observer fires on its own (first delivery after `observe`), so the
 *   window is short; it is the one place in this hook where a width outlives its
 *   element, and B.5l pins it.
 *
 * @useWhen
 *   - A surface must know how much room its own container has (column choice, rail, edge hint)
 *   - The viewport is the wrong signal because the container moves independently of it
 *
 * @dontUseFor
 *   - The device switch card ↔ table — that one stays at the VIEWPORT breakpoint `sm` (NW3.6:553)
 *   - The N9.6 edge hint — that one hangs on `scrollWidth > clientWidth` (§3.4:513-514), not on `isNarrow`
 *   - A one-off read (`el.getBoundingClientRect()` inside a handler) — this hook is for a live value
 *
 * @related
 *   - useColumnPriority (BW5) — takes `width` as an argument and turns it into visible columns
 *   - useOverflowMask (BS15) — the other observer in this folder; it watches the SCROLL position, not the width
 *
 * @reuseHints
 *   - ✅ `KpiRow` became a consumer in G0b.5 (2026-10-13). It used to carry the SAME observer
 *     logic inline — the weaker copy, without the rAF throttle and the loop guard. That copy
 *     is gone; `{ width }` fits its `k` computation one-to-one (`KpiRow.jsx`, probe 11 in
 *     `KpiRow.test.jsx` waits for the throttled frame). One source of the mechanic, not two
 *   - The detail rail (`DETAIL_RAIL_WIDTH`, BW7) once it picks 320 vs 640 from the room
 *   - `TableViewport` (BS14) takes `containerWidth` since G0b.4 (BW6); the Musterroute is the
 *     first caller (`ListenMuster.jsx`, G0b.5)
 *
 * @param {object} ref React ref to the container element whose inner width is measured.
 *   The element may appear LATER than the first render — the observer re-attaches.
 * @returns {{ width: number|null, isNarrow: boolean }} `width` is `null` until the first
 *   measurement (unknown ≠ 0); `isNarrow` is `true` exactly when a width has been measured
 *   and lies below `NARROW_BELOW`.
 */
export default function useContainerWidth(ref) {
  const [width, setWidth] = useState(null)
  // Zuletzt ANGEWANDTE Breite — die Vergleichsgrundlage des Schleifenschutzes.
  const zuletztGemessen = useRef(null)
  // Die juengste Breite, die auf ihr Bild wartet (die Drosselung wirft keine weg).
  const anstehend = useRef(null)
  const rahmen = useRef(null)
  // Das Element, an dem der Beobachter GERADE haengt — die Vergleichsgrundlage des Umhaengens.
  const angehaengt = useRef(null)
  const beobachter = useRef(null)

  // 🔴 Ohne Abhaengigkeiten: laeuft nach JEDEM Render und haengt den Beobachter um,
  //    sobald `ref.current` ein anderes Element traegt. Genau das holt den Behaelter,
  //    der erst im zweiten Bild montiert wird (bedingt gerendert: Karte, Reiter) —
  //    ein Effekt an `[ref]` saehe ihn nie. Der Vergleich ist billig und idempotent.
  useEffect(() => {
    const el = ref?.current ?? null
    if (el === angehaengt.current) return

    // Umhaengen: erst ab, dann an.
    abhaengen(beobachter, rahmen, anstehend, angehaengt)
    angehaengt.current = el

    // ⚠️ Der zweite Zweig ist unter Jest TOT: `setupTests.js:123-129` setzt einen
    //    Stummel, `typeof ResizeObserver` ist dort also nie `'undefined'`. Er traegt
    //    eine Umgebungszusage (jsdom hat kein Layout und keinen Beobachter) und ist
    //    kein Verhaltensfehler — er darf nur nicht fuer geprueft gehalten werden.
    //    Wer ihn pruefen will, loescht den Stummel in SEINEM Fall (`delete
    //    window.ResizeObserver`, der Hausweg aus `setupTests.js:114-122`).
    if (!el || typeof ResizeObserver === 'undefined') return

    // Ein NEUES Element wird immer einmal gemessen — ohne diese Zeile blockierte
    // der Schleifenschutz die erste Messung, wenn sie zufaellig dieselbe Breite traegt.
    zuletztGemessen.current = null

    beobachter.current = new ResizeObserver((eintraege) => {
      // Der letzte Eintrag ist der juengste Stand; aeltere desselben Aufrufs sind ueberholt.
      const breite = eintraege[eintraege.length - 1]?.contentRect?.width
      // Eine 0 heisst „nicht ausgelegt", nicht „null breit" (`KpiRow.jsx:300-307`).
      if (!Number.isFinite(breite) || breite <= 0) return
      // 🔴 Schleifenschutz (NW4.6:652): dieselbe Breite startet keinen weiteren Lauf.
      if (breite === zuletztGemessen.current) return
      anstehend.current = breite
      // 🔴 Drosselung: hoechstens EIN Bild je Breitenfolge.
      if (rahmen.current != null) return
      rahmen.current = requestAnimationFrame(() => {
        rahmen.current = null
        const neu = anstehend.current
        anstehend.current = null
        if (neu == null || neu === zuletztGemessen.current) return
        zuletztGemessen.current = neu
        setWidth(neu)
      })
    })
    beobachter.current.observe(el)
  })

  // Nur der Abbau raeumt auf — der Effekt oben hat keinen eigenen Abbau, sonst
  // haenge er den Beobachter bei JEDEM Render ab und wieder an.
  useEffect(() => () => abhaengen(beobachter, rahmen, anstehend, angehaengt), [])

  return { width, isNarrow: width !== null && width < NARROW_BELOW }
}
